import { find, remove, unionBy } from 'lodash';
import React, { useEffect } from 'react';
import { useState } from 'react';
import GridLayout from 'react-grid-layout';
import { IoIosFlag } from 'react-icons/io';
import { BsFillGearFill } from 'react-icons/bs';

import * as defaults from '../utils/defaults';
import Menu from '../components/Menu';
import { Color, defaultColor } from '../utils/colors';
import { addDays, dayShortNames, monthNames, subtractDays } from '../utils/date';
import { getDatesFromPos, getScaleFromMode, getOffsetForDate, ScaleMode } from '../utils/timeline';
import useScrollInfo, { ScrollInfo } from '../utils/useScrollInfo';
import BarComponent, { Bar } from './Bar';
import BarEditor from './BarEditor';
import MilestoneEditor from './MilestoneEditor';

export type Timeline = {
  id: string;
  title: string;
  highlightWeekends: boolean;
  shouldCompact: boolean;
  firstDate: Date;
  totalDays: number;
  bars: Bar[];
  layout: Position[];
  milestones: Milestone[];
  screaming: boolean;
  scaleMode: ScaleMode;
  initialAnchorDate?: Date;
};

export type Position = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minH: number;
  maxH: number;
};

export type Milestone = {
  id: string;
  title: string;
  date: Date;
};

const barHeight = 64;
const barMargin = 10;
const lanesTopMargin = 105; //px — margin above the first lane (basically the height of the day markers etc.)

// const bars = buildBars(timeline.bars);

const buildBarsMapFromArray = (bars: Bar[]): { string: Bar } => {
  const barsMap = {} as { string: Bar };
  bars.forEach(bar => (barsMap[bar['id']] = bar));
  return barsMap;
};

const buildMilestonesMapFromArray = (milestones: Milestone[]): { string: Milestone } => {
  const milestonesMap = {} as { string: Milestone };
  milestones.forEach(milestone => (milestonesMap[milestone.date.toDateString()] = milestone));
  return milestonesMap;
};

const Timeline = ({
  firstDate,
  setFirstDate,
  totalDays,
  setTotalDays,
  bars,
  setBars,
  layout,
  setLayout,
  milestones,
  setMilestones,
  scaleMode,
  setScaleMode,
  highlightWeekends,
  setHighlightWeekends,
  shouldCompact,
  setShouldCompact,
  screaming,
  setScreaming,
  editable = true,
  initialAnchorDate = undefined,
}: {
  firstDate: Date;
  setFirstDate: any;
  totalDays: number;
  setTotalDays: any;
  bars: Bar[];
  setBars: any;
  layout: Position[];
  setLayout: any;
  milestones: Milestone[];
  setMilestones: any;
  scaleMode: ScaleMode;
  setScaleMode: any;
  highlightWeekends: boolean;
  setHighlightWeekends: any;
  shouldCompact: boolean;
  setShouldCompact: any;
  screaming: boolean;
  setScreaming: any;
  editable?: boolean;
  initialAnchorDate?: Date;
}) => {
  const [detectEdges, setDetectEdges] = useState(true); // Move to the parent component to remove console warning
  const [editingBarInfo, setEditingBarInfo] = useState(null as any);
  const [editingMilestoneInfo, setEditingMilestoneInfo] = useState(null as any);
  const [knobsVisible, setKnobsVisible] = useState<boolean>(false);
  const [showHandle, setShowHandle] = useState<string | null>(null);
  const [hoveringDate, setHoveringDate] = useState<string | null>(null);
  const [scrollInfo, setRef, gridRef] = useScrollInfo() as [
    ScrollInfo,
    () => void,
    React.RefObject<HTMLDivElement>
  ];

  const settings = [
    { key: 'weekends', value: 'Highlight weekends', selected: highlightWeekends },
    { key: 'compact', value: 'Auto compact', selected: shouldCompact },
    { key: 'scream', value: 'Everything’s on fire 🔥', selected: screaming },
  ];

  const widthScale = getScaleFromMode(scaleMode);
  const todayOffset = getOffsetForDate(firstDate, new Date());
  const scaleOptions = [
    { key: 'month', value: 'Month', selected: scaleMode === 'month' },
    { key: '2-weeks', value: '2-Weeks', selected: scaleMode === '2-weeks' },
    { key: 'week', value: 'Week', selected: scaleMode === 'week' },
  ];

  const barsMap = buildBarsMapFromArray(bars);
  const milestonesMap = buildMilestonesMapFromArray(milestones);

  if (
    detectEdges &&
    (scrollInfo.x.percentage === 0 || scrollInfo.x.percentage === 1) &&
    scrollInfo.x.direction !== 0 &&
    editable
  ) {
    const daysToAdd = 5;
    setDetectEdges(false);
    setTotalDays(totalDays + daysToAdd);
    if (scrollInfo.x.direction === -1) {
      setFirstDate(subtractDays(firstDate, daysToAdd));
      const newLayout = [] as Position[];
      layout.forEach(item => newLayout.push({ ...item, x: item.x + daysToAdd }));
      setLayout(newLayout);
      if (gridRef && gridRef.current) {
        gridRef.current.scrollLeft = daysToAdd * widthScale;
      }
    }
    setTimeout(() => setDetectEdges(true), 1000);
  }

  const createNewItem = ({
    x,
    y,
    w,
    openEditor = false,
    clientX = undefined,
    clientY = undefined,
    barToClone = {},
  }) => {
    const id = Date.now().toString();
    const newItem = { i: id, x, y, w, h: 1, minH: 1, maxH: 1 };
    const newBar: Bar = {
      title: '',
      color: defaultColor as Color,
      done: false,
      ...barToClone,
      id,
      _id: undefined,
      ...getDatesFromPos(firstDate, newItem.x, newItem.w),
    };

    console.log('newBar', newBar);
    setLayout([...layout, newItem]);
    setBars(unionBy([newBar], bars, 'id'));

    if (openEditor) {
      setTimeout(() => setEditingBarInfo({ id, x: clientX, y: clientY }), 100);
    }
  };

  const handleGridClick = e => {
    if (!editable) {
      return;
    }
    const xOffset =
      (e.clientX - e.currentTarget.getBoundingClientRect().x + e.currentTarget.scrollLeft) /
      widthScale;
    const yOffset =
      (e.clientY -
        e.currentTarget.getBoundingClientRect().y +
        e.currentTarget.scrollTop -
        lanesTopMargin) /
      (barHeight + barMargin);
    const timeOffset = Math.floor(xOffset);
    const laneOffset = Math.floor(yOffset);

    if (laneOffset < 0 || timeOffset < 0) {
      return;
    }

    for (const item of layout) {
      if (item.y === laneOffset) {
        if (item.x <= timeOffset && item.x + item.w > timeOffset) {
          return;
        }
      }
    }

    createNewItem({
      x: timeOffset,
      y: laneOffset,
      w: 2,
      openEditor: true,
      clientX: e.clientX,
      clientY: e.clientY,
    });
  };

  const handleBarClick = e => {
    if (!editable) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const id = e.currentTarget.dataset.id;
    if (e.altKey) {
      console.log('Duplicating');
      const layoutToCopy: Position = find(layout, { i: id });
      const barToClone = barsMap[id];
      console.log(barToClone);
      createNewItem({
        x: layoutToCopy.x + layoutToCopy.w,
        y: layoutToCopy.y,
        w: layoutToCopy.w,
        openEditor: false,
        barToClone,
      });
    } else if (e.metaKey) {
      const bar = { ...barsMap[id], done: !barsMap[id].done };
      setBars(unionBy([bar], bars, 'id'));
    } else {
      setTimeout(() => {
        setEditingBarInfo({ id, x: rect.x, y: rect.y });
      }, 100);
    }
  };

  const onEditBar = (id, newBar, keepEditing) => {
    if (!keepEditing) {
      setEditingBarInfo(null);
    }
    if (!newBar?.title && !keepEditing) {
      remove(bars, b => b.id === id);
      remove(layout, l => l.i === id);
      setBars([...bars]);
      setLayout([...layout]);
    } else {
      const bar = { ...barsMap[id], ...newBar };
      setBars(unionBy([bar], bars, 'id'));
    }
  };

  const createNewMilestone = ({ dateString }) => {
    const id = dateString;
    const date = new Date(dateString);
    const milestone: Milestone = {
      id,
      title: '',
      date,
    };
    setMilestones(unionBy([milestone], milestones, 'id'));
  };

  const onEditMilestone = (id, newMilestone, keepEditing) => {
    if (!keepEditing) {
      setEditingMilestoneInfo(null);
    }
    if (!newMilestone?.title && !keepEditing) {
      remove(milestones, m => m.id === id);
      setMilestones([...milestones]);
    } else {
      const milestone = { ...milestonesMap[id], ...newMilestone };
      setMilestones(unionBy([milestone], milestones, 'id'));
    }
  };

  const handleDayClick = e => {
    if (!editable) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const dateString = e.currentTarget.dataset.date;
    if (!milestonesMap[dateString]) {
      createNewMilestone({ dateString });
    }
    setTimeout(() => {
      setEditingMilestoneInfo({ mapKey: dateString, x: rect.x + widthScale / 2, y: rect.y });
    }, 100);
  };

  const onMoveComplete = (_1, _2, newItem) => {
    const id = newItem.i;
    const bar = { ...barsMap[id], ...getDatesFromPos(firstDate, newItem.x, newItem.w) };
    setBars(unionBy([bar], bars, 'id'));
  };

  const onDrag = () => {
    setEditingBarInfo(null);
  };

  const handleTodayClick = () => {
    if (gridRef && gridRef.current) {
      gridRef.current.scrollLeft = todayOffset * widthScale - gridRef.current.clientWidth / 2;
    }
  };
  const handleMouseEnter = () => setKnobsVisible(true);
  const handleMouseLeave = () => setKnobsVisible(false);
  const handleBarMouseEnter = e => setShowHandle(e.currentTarget?.dataset?.id);
  const handleBarMouseLeave = () => setShowHandle(null);
  const handleDayMouseEnter = e => setHoveringDate(e.currentTarget?.dataset?.date);
  const handleDayMouseLeave = () => setHoveringDate(null);

  const handleSelectSetting = newSettingKey => {
    // Update parent
    const idx = settings.findIndex(obj => obj.key === newSettingKey);
    const newSetting = !settings[idx].selected;
    settings[idx].selected = newSettingKey;
    // setSettings([...settings]);
    if (newSettingKey === 'weekends') {
      setHighlightWeekends(newSetting);
    } else if (newSettingKey === 'compact') {
      setShouldCompact(newSetting);
    } else if (newSettingKey === 'scream') {
      setScreaming(newSetting);
    }
  };

  useEffect(() => {
    let anchorDate;
    if (initialAnchorDate) {
      anchorDate = initialAnchorDate;
    } else {
      anchorDate = new Date();
    }
    const offSet = getOffsetForDate(firstDate, anchorDate);
    setTimeout(() => {
      if (gridRef && gridRef.current) {
        gridRef.current.scrollLeft = offSet * widthScale - gridRef.current.clientWidth / 2;
      }
    }, 200);
  }, [scaleMode]);

  return (
    <div
      className="w-full mx-auto text-xl font-light text-center text-gray-700"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div>
        <div className="flex items-center justify-end ">
          <div
            className={`flex z-30 transition-opacity duration-200 ${
              knobsVisible ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="w-8 h-8 bg-gradient-to-r from-transparent to-white"></div>
            {editable && (
              <div className="flex items-center px-2 bg-white divide-x divide-gray-400">
                <Menu
                  title={find(scaleOptions, { key: scaleMode }).value}
                  options={scaleOptions}
                  onSelect={setScaleMode}
                />
                <a
                  className="px-3 py-1 text-sm font-semibold text-gray-800 cursor-pointer hover:text-gray-500"
                  onClick={handleTodayClick}
                >
                  Today
                </a>
                <Menu icon={<BsFillGearFill />} options={settings} onSelect={handleSelectSetting} />
              </div>
            )}
          </div>
        </div>
        <div className="w-auto text-xl font-light text-gray-700 max-w-none">
          <div className="-mt-10">
            <div className="sticky w-full mt-10 border-t border-gray-400"></div>
            <div
              className="relative w-full pt-12 pb-6 -mt-10 overflow-x-scroll overflow-y-hidden disable-scrollbar"
              style={{ minHeight: '350px' }}
              onMouseDown={handleGridClick}
              ref={setRef}
            >
              {/* <div className="relative w-full overflow-x-scroll overflow-y-hidden disable-scrollbar"> */}
              <div
                className="absolute h-full bg-black bg-opacity-25"
                style={{
                  top: '5.65rem',
                  left: `${3 + widthScale / 2 + (widthScale - 0.1) * todayOffset}px`,
                  width: '2px',
                }}
              >
                <div
                  className="relative bg-gray-500 rounded-full"
                  style={{ width: '10px', height: '10px', left: '-4px' }}
                ></div>
              </div>

              <div className="absolute flex h-full">
                {[...Array(totalDays)].map((_, i) => {
                  const date = addDays(firstDate, i);
                  const day = date.getDay();
                  const dayStr = dayShortNames[date.getDay()];
                  const dayName = dayShortNames[day];
                  const sunday = day === 0;
                  const weekend = dayName === 'S';
                  // const offset = -1;
                  const milestone = milestonesMap[date.toDateString()];
                  const milestoneOffset =
                    scaleMode === 'week' ? 10 : scaleMode === '2-weeks' ? 5 : 2;
                  const dateNumber = date.getDate();
                  const dateKey = date.toDateString();
                  const hovering = hoveringDate === dateKey;
                  const flagAlign =
                    scaleMode === 'week'
                      ? 'justify-start -ml-2'
                      : scaleMode === 'month'
                      ? 'justify-start -ml-1'
                      : 'justify-center';
                  const align =
                    scaleMode === 'week' ? 'start' : scaleMode === 'month' ? 'start' : 'center';

                  const shadedBg = weekend && highlightWeekends;
                  const showDivider = scaleMode === 'week' || sunday;
                  return (
                    <React.Fragment key={`weekends-${i}`}>
                      {(i === 0 || dateNumber === 1) && (
                        <div className="sticky left-0 text-sm font-semibold">
                          <div
                            className="absolute w-48 px-2 -ml-2 text-left bg-white"
                            style={{ top: '-2.25rem' }}
                          >
                            {monthNames[date.getMonth()]}
                            <span className="ml-1 text-gray-500">{date.getFullYear()}</span>
                          </div>
                        </div>
                      )}
                      <div
                        className={`relative h-full text-center`}
                        style={{
                          minWidth: `${widthScale}px`,
                          maxWidth: `${widthScale}px`,
                        }}
                      >
                        <div
                          key={`date-labels-${i}`}
                          data-date={dateKey}
                          onMouseEnter={handleDayMouseEnter}
                          onMouseLeave={handleDayMouseLeave}
                          onClick={handleDayClick}
                          className={`text-xs flex pt-2 h-14 justify-start items-${align} flex-col font-semibold ${
                            editable ? 'cursor-pointer' : ''
                          } text-gray-600 ${scaleMode !== '2-weeks' ? 'text-left' : 'ml-2'} ${
                            hovering ? 'z-10' : ''
                          }`}
                        >
                          <span
                            className={`${weekend && highlightWeekends ? 'text-gray-400' : ''}`}
                          >
                            {dateNumber}
                          </span>
                          {editable && hovering && !milestone && (
                            <div className={`flex w-full ${flagAlign}`}>
                              <div className="flex items-center justify-center w-6 h-6 bg-gray-200 rounded">
                                <IoIosFlag className="text-red-700 text-md" />
                              </div>
                            </div>
                          )}
                          {(!hovering || !editable) && !milestone && (
                            <span className="text-gray-400">{dayStr}</span>
                          )}
                        </div>
                        <div
                          className={`h-full ${
                            editable ? 'hover:bg-gray-300 hover:bg-opacity-25' : ''
                          } text-center ${shadedBg ? 'bg-gray-300 bg-opacity-25' : ''} ${
                            showDivider ? 'border-r border-gray-500 border-opacity-50' : ''
                          }`}
                        ></div>
                      </div>
                      {milestone && (
                        <div
                          className="absolute h-full overflow-visible bg-red-700"
                          style={{
                            top: '1.9rem',
                            left: `${milestoneOffset + widthScale * i}px`,
                            width: '2px',
                          }}
                        >
                          <div
                            className={`${
                              hoveringDate === date.toDateString() ? 'z-20' : 'z-10'
                            } absolute -mt-1 text-xs font-normal overflow-hidden text-white bg-white rounded-t rounded-r ${
                              editable ? 'cursor-pointer' : ''
                            }`}
                            data-date={date.toDateString()}
                            style={{
                              maxWidth: `${
                                hoveringDate === date.toDateString()
                                  ? '300'
                                  : widthScale - milestoneOffset
                              }px`,
                              // left: `${milestoneOffset + widthScale * i}px`,
                              width: 'fit-content',
                            }}
                            onMouseEnter={handleDayMouseEnter}
                            onMouseLeave={handleDayMouseLeave}
                            onClick={handleDayClick}
                          >
                            <div className="w-full pl-2 pr-1 text-left truncate bg-red-700 rounded-t rounded-r ">
                              {milestone.title ? (
                                milestone.title
                              ) : (
                                <span className="text-white opacity-25">New item</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <GridLayout
                className="mt-16 pointer-events-none"
                layout={layout}
                cols={totalDays}
                rowHeight={barHeight}
                width={totalDays * widthScale}
                margin={[barMargin, barMargin]}
                compactType={shouldCompact ? 'vertical' : null}
                containerPadding={[0, 0]}
                onDrag={onDrag}
                onDragStop={onMoveComplete}
                onResizeStop={onMoveComplete}
                onLayoutChange={setLayout}
                isDraggable={editable}
                isResizable={editable}
              >
                {layout.map(b => {
                  if (!b) {
                    return null;
                  }
                  const bar = barsMap[b.i];
                  return (
                    <div
                      key={b.i}
                      className={`pointer-events-auto ${
                        detectEdges ? '' : 'transition-none-important'
                      }`}
                      data-id={b.i}
                      onMouseEnter={handleBarMouseEnter}
                      onMouseLeave={handleBarMouseLeave}
                    >
                      <div className="relative" onMouseDown={handleBarClick} data-id={b.i}>
                        <BarComponent bar={bar} editable={editable} />
                        {editable && showHandle === b.i && (
                          <div className="absolute top-0 right-0 flex flex-col w-3 h-full">
                            <div className="flex-grow my-2 mr-2 bg-white rounded opacity-25"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </GridLayout>
            </div>
          </div>
        </div>
      </div>
      {editingBarInfo && (
        <BarEditor
          x={editingBarInfo?.x}
          y={editingBarInfo?.y}
          id={editingBarInfo?.id}
          bar={barsMap[editingBarInfo?.id]}
          isOpen={!!editingBarInfo}
          onEdit={onEditBar}
        />
      )}
      {editingMilestoneInfo && (
        <MilestoneEditor
          x={editingMilestoneInfo?.x}
          y={editingMilestoneInfo?.y}
          milestone={milestonesMap[editingMilestoneInfo?.mapKey]}
          isOpen={!!editingMilestoneInfo}
          onEdit={onEditMilestone}
        />
      )}
    </div>
  );
};

export default Timeline;

export const NonEditableTimeline = props => {
  const noOp = () => {};
  return (
    <Timeline
      bars={props.bars}
      setBars={noOp}
      firstDate={props.firstDate}
      setFirstDate={noOp}
      highlightWeekends={true}
      setHighlightWeekends={noOp}
      layout={props.layout}
      setLayout={noOp}
      milestones={props.milestones}
      setMilestones={noOp}
      scaleMode={defaults.scaleMode}
      setScaleMode={noOp}
      screaming={false}
      setScreaming={noOp}
      shouldCompact={props.shouldCompact}
      setShouldCompact={noOp}
      totalDays={props.totalDays}
      setTotalDays={noOp}
      initialAnchorDate={props.initialAnchorDate}
      editable={false}
    />
  );
};
